import React from 'react'
import { connect } from 'react-redux'
import { fetchChanges } from '../../../action/changes-last-called-actions'
import { followingFetchRequest } from '../../../action/follow-list-actions'
import { layersFetchRequest } from '../../../action/layer-actions'
import { markerGroupsFetchRequest } from '../../../action/marker-group-actions'
import { markerTypes105FetchRequest } from '../../../action/marker-types-105-actions'
import { markerTypes35FetchRequest } from '../../../action/marker-types-35-actions'
import { markerTypes25FetchRequest } from '../../../action/marker-types-25-actions'
import { residenceGetRequest } from '../../../action/residence-actions'
import { userFetchRequest } from '../../../action/user-actions'
import { userSettingsGetRequest, userSettingsSet, userSettingsUpdateRequest } from '../../../action/user-settings-actions'
import { setLoading as setLoadingAction } from '../../../action/loading-actions'
import Map from '../../map'
import MainUI from '../../main-ui/'
import { collapseLeftToolbar } from '../../../action/left-toolbar-state-actions'
import { collapseRightToolbar } from '../../../action/right-toolbar-state-actions'
import {
  reconcileLayerSettings, ACTIVE_LAYERS_SETTING_ID, FAVORITE_LAYERS_SETTING_ID,
} from '../../../lib/layerSettings'
import { termsFetchRequest } from '../../../action/terms-actions'
import moment from 'moment'
import { authRefreshRequest, logout, expiredTokenLogout } from '../../../action/auth-actions'
import { getNotification } from '../../../action/notification-actions'
import { getStates } from '../../../action/state-actions'
import { weatherActionRemove } from '../../../action/weather-action'
import { removeCenterMarker } from '../../../action/center-marker-action'
import { selectedObjectIdNull } from '../../../action/map-object-id'
import MapMarkersUpdater from './realtime-update'
import AppMessage from '../app-message'
import TopMenu from '../../top-menu'
import { selectedTabAction } from '../../../action/selected-tab-action'
import { userActivePlan } from '../../../action/huntplanner-subscription-actions'
import { userHasProUltimate, userHasPro, userHasProAdvantage } from '../../../lib/util'
import { expandMorefilterMenu } from '../../../action/huntplanner-morefilters-actions'
import { huntPlannerRightMenuBarHide } from '../../../action/huntplanner-menu-bar'
import { isHuntPlannerEnable, isHuntPlannerDisable } from '../../../action/isHuntPlannerDisable'
import { setHuntPlannerMapReferenceNull } from '../../../action/map-reference-huntplanner-action'
import { setLandTrustMapReferenceNull } from '../../../action/map-reference-landtrust'
import { userSubscriptionFetchRequest } from '../../../action/user-subscription-actions'
import { unselected3dMapAction } from '../../../action/selected-3dmap'
import { openThreeDWelcomePopup } from '../../../action/threed-maps-actions'
import { huntPlannerActiveOptions } from '../../../action/huntplanner-selected-options'
import { fetchGetUnitDetails } from '../../../action/huntPlanner-unitdetails-actions'
import { fetchAppMessage as fetchAppMessageAction } from '../../../action/app-message-actions'
import { huntPlannerRightMenuBarOpen } from '../../../action/huntplanner-menu-bar'
import { stopMapLoading, startMapLoading } from '../../../action/huntplanner-map-loader'
import { openMapDeepLink } from '../../../action/map-deep-link-actions'
import { parseMapDeepLink, withoutMapDeepLink } from '../../../lib/map-deep-link'
import { alertDeepLink } from '../../../lib/artemis/alerts'

// Lazy-loaded tab panels — only downloaded when the user first opens that tab
const HuntPlannerLandingPage = React.lazy(() => import('../../hunt-planner/landingpage/huntPlannerLandingPage'))
const RecreationalAccess = React.lazy(() => import('../../recreationAccess/landingpage/index'))
const GearStore = React.lazy(() => import('../../gearStore/landingpage/index'))
const ArtemisFullView = React.lazy(() => import('../../artemis-functionality/full-view'))

const TRANSITION_STYLE = 'opacity 1000ms ease-in-out, visibility 0ms 1000ms'

// Stable style objects — same reference when tab state unchanged, preventing unnecessary child re-renders
const STYLES = {
  tabHidden: { display: 'none', opacity: 0, visibility: 'hidden', transition: TRANSITION_STYLE },
  tabVisible: { display: 'flex', opacity: 1, visibility: 'visible', transition: TRANSITION_STYLE },
  mapHidden: { display: 'none', opacity: 0, visibility: 'hidden', transition: TRANSITION_STYLE },
  mapVisible: { display: 'flex', opacity: 1, visibility: 'visible', transition: TRANSITION_STYLE },
}

const PLAN_IDS = {
  PRO_ULTIMATE: 44,
  PRO_ADVANTAGE: 36,
  PRO: 4,
  FREE: 0,
}

// Returned when a safeCall times out or throws, so callers can tell "finished" from
// "gave up on". Never treat a FAILED result as though the call succeeded — validateLayers
// deletes the user's saved layers if it acts on a layer catalog that never arrived (WEB-7030).
const FAILED = Symbol('failed')

// Per-call timeout guard used inside Promise.allSettled — skips slow calls instead of blocking all loading
const safeCall = (fn, label) =>
  Promise.race([
    Promise.resolve().then(fn),
    new Promise(res => setTimeout(() => res(FAILED), 5000)),
  ]).catch(err => {
    console.warn(`"${label}" failed, skipping.`, err)
    return FAILED
  })

const SPLASH_SCREEN = (
  <div className="splash-screen">
    <div className="splash-screen-spinner-container">
      <div className="loader-spinner"></div>
    </div>
  </div>
)

class Landing extends React.PureComponent {
  constructor(props) {
    super(props)
    this.handleFetchChanges = () => this.props.fetchChanges()
    this.state = {
      loaderSpinner: true,
    }
  }

  componentDidMount() {
    this._mounted = true
    // Recover the auth token when the tab regains focus after the machine wakes
    // from sleep — the scheduled refresh timer is suspended during sleep (WEB-7002).
    document.addEventListener('visibilitychange', this.handleVisibility)
    this.props.setLoading(true)

    const urlParams = new URLSearchParams(window.location.search)
    const hasDeeplinkParams =
      urlParams.has('StateAbbreviation') &&
      urlParams.has('Species') &&
      urlParams.has('HuntTypeKey') &&
      urlParams.has('unitName')

    if (hasDeeplinkParams) {
      const payload = {
        StateAbbreviation: urlParams.get('StateAbbreviation') || '',
        Species: urlParams.get('Species') || '',
        HuntTypeKey: urlParams.get('HuntTypeKey') || '',
        unitName: urlParams.get('unitName') || '',
      }
      this.props.selectedTabAction('huntplanner')
      this.props.huntPlannerActiveOptions(payload)
      window.history.replaceState({}, document.title, window.location.pathname)
      this.props.setLoading(false)
      this.setState({ loaderSpinner: false })
      return
    }

    // An "Open in BaseMap" link (WEB-7109). Read now - a signed-out user got here through the
    // in-place <Login />, so the params are still in the URL - and taken out of the address bar at
    // once so a reload doesn't open it again. It is applied once the map's data has loaded.
    // An Artemis invite link (/artemis?invite=TOKEN, group chats): kept for the ARTEMIS tab, which joins
    // the session once the app has loaded, and taken out of the address bar.
    const artemisInvite = window.location.pathname.startsWith('/artemis') && urlParams.get('invite')
    if (artemisInvite) {
      try { window.sessionStorage.setItem('artemis.invite', artemisInvite) } catch (e) { /* private window: the link just opens the app */ }
      window.history.replaceState({}, document.title, '/')
    }
    // An Artemis alert link (/artemis?conversation=ID&message=ID, WEB-7145: alert emails, pushes and texts, and
    // @mention pushes): kept the same way for the ARTEMIS tab, which opens that conversation at that message.
    const artemisOpen = window.location.pathname.startsWith('/artemis') && !artemisInvite ? alertDeepLink(window.location.search) : null
    if (artemisOpen) {
      try { window.sessionStorage.setItem('artemis.open', JSON.stringify(artemisOpen)) } catch (e) { /* private window: the link just opens the app */ }
      window.history.replaceState({}, document.title, '/')
    }

    this._mapDeepLink = parseMapDeepLink(window.location.search)
    if (this._mapDeepLink) {
      window.history.replaceState({}, document.title, window.location.pathname + withoutMapDeepLink(window.location.search))
    }

    this.initializeApp()
    if (artemisInvite || artemisOpen) this.props.selectedTabAction('artemis')
  }

  componentDidUpdate(prevProps) {
    if (prevProps.selectedTabvalue !== this.props.selectedTabvalue) {
      if (this.props.selectedTabvalue === 'map') {
        this.props.setLandTrustMapReferenceNull()
        this.props.setHuntPlannerMapReferenceNull()
      }
    }
  }

  componentWillUnmount() {
    this._mounted = false
    clearTimeout(this._refreshTimer)
    document.removeEventListener('visibilitychange', this.handleVisibility)
  }

  initializeApp = async () => {
    const { setLoading } = this.props

    // Sync UI resets
    this.props.collapseLeftToolbar()
    this.props.collapseRightToolbar()
    this.props.weatherActionRemove()
    this.props.selectedTabAction('map')
    this.props.isHuntPlannerDisable()
    this.props.unselected3dMapAction()
    this.props.openThreeDWelcomePopup()
    this.updateAuthToken()

    // userFetchRequest must complete first — map components call userHasPro(user)
    // which crashes if user is undefined in Redux state
    await safeCall(() => this.props.userFetchRequest(), 'userFetchRequest')

    // Open the map immediately after user is loaded
    setLoading(false)
    this.setState({ loaderSpinner: false })

    // Kept separate so its outcome can gate validateLayers() — pruning saved layers
    // against a catalog that never loaded wipes them permanently (WEB-7030).
    const layersCall = safeCall(() => this.props.layersFetchRequest(), 'layersFetchRequest')

    // All remaining API calls run in the background after map is visible
    Promise.allSettled([
      safeCall(() => this.props.followingFetchRequest(), 'followingFetchRequest'),
      layersCall,
      safeCall(() => this.props.markerGroupsFetchRequest(), 'markerGroupsFetchRequest'),
      safeCall(() => this.props.markerTypes25FetchRequest(), 'markerTypes25FetchRequest'),
      safeCall(() => this.props.markerTypes35FetchRequest(), 'markerTypes35FetchRequest'),
      safeCall(() => this.props.markerTypes105FetchRequest(), 'markerTypes105FetchRequest'),
      safeCall(() => this.props.residenceGetRequest(), 'residenceGetRequest'),
      safeCall(() => this.props.termsFetchRequest(), 'termsFetchRequest'),
      safeCall(() => this.props.getStates(), 'getStates'),
      safeCall(() => this.props.fetchChanges(), 'fetchChanges'),
      safeCall(() => this.props.userSettingsGetRequest(), 'userSettingsGetRequest'),
      safeCall(() => this.props.userSubscriptionFetchRequest(), 'userSubscriptionFetchRequest'),
    ]).then(async () => {
      if (!this._mounted) return
      this.props.removeCenterMarker()
      this.props.selectedObjectIdNull()
      this.props.expandMorefilterMenu(false)
      this.props.huntPlannerRightMenuBarHide()
      this.props.isHuntPlannerEnable()
      if ((await layersCall) !== FAILED) this.validateLayers()
      // After validateLayers, so the link's layers are checked against the loaded catalog and
      // settings, and after the reset of the selected object above, so the linked one stays open.
      if (this._mapDeepLink) {
        this.props.openMapDeepLink(this._mapDeepLink)
        this._mapDeepLink = null
      }
      this.props.getNotifications()
      this.fetchSubscription()
      this.refreshToken()

      // Once per load of the map, mirroring MainActivity.fetchAppMessage and
      // MapViewController.syncAppMessage — both fire on entering the main map, online.
      // Deliberately last and deliberately unrepeated: the request itself records the
      // view, so calling it twice costs the user one of their allotted views (WEB-7079).
      this.props.fetchAppMessage()
    })
  }

  updateAuthToken = () => {
    const { expires } = this.props.auth
    const timeDifference = new Date(expires).getTime() - Date.now()
    if (timeDifference <= 0) {
      this.props.logout()
    }
  }

  // Drop saved layer ids that no longer exist in the catalog, and persist the result.
  //
  // This is destructive and cross-device: the value written here is what mobile syncs
  // down. So it must only ever run against a catalog we know is complete — an empty
  // layersDictionary means the layer fetch never landed, NOT that the user's layers are
  // all invalid. Acting on that emptiness silently deleted every saved layer, on web and
  // on the user's phone (WEB-7030).
  validateLayers() {
    const { userSettings, layersDictionary } = this.props
    const {
      skip, active: finalActive, favorite: validFavorite, activeChanged, favoriteChanged,
    } = reconcileLayerSettings({ userSettings, layersDictionary })

    // Catalog or settings not loaded — skipping is always safe, the next load with a
    // populated catalog prunes correctly.
    if (skip) return
    if (!activeChanged && !favoriteChanged) return

    const copy = userSettings.map(setting => {
      if (setting.settingId === ACTIVE_LAYERS_SETTING_ID) {
        return { ...setting, value: JSON.stringify(finalActive), valueArray: finalActive }
      }
      if (setting.settingId === FAVORITE_LAYERS_SETTING_ID) {
        return { ...setting, value: JSON.stringify(validFavorite), valueArray: validFavorite }
      }
      return setting
    })

    if (activeChanged) this.props.userSettingsUpdateRequest(ACTIVE_LAYERS_SETTING_ID, JSON.stringify(finalActive))
    if (favoriteChanged) this.props.userSettingsUpdateRequest(FAVORITE_LAYERS_SETTING_ID, JSON.stringify(validFavorite))
    this.props.userSettingsSet(copy)
  }

  // Schedule the next token refresh. Kept resilient so a single failed refresh —
  // or a timer that was suspended while the machine slept — can't permanently
  // break the app until the user reloads (WEB-7002).
  refreshToken = () => {
    if (this._refreshTimer) { clearTimeout(this._refreshTimer); this._refreshTimer = null }
    const expiry = moment.utc(this.props.auth.expires).diff(moment())
    if (expiry && expiry > 2000000000) {
      // Token valid far in the future — re-check periodically (also guards against
      // setTimeout's ~24.8-day max-delay overflow).
      this._refreshTimer = setTimeout(this.refreshToken, 1000000)
    } else if (expiry && expiry > 60000) {
      // Refresh ~1 min before expiry.
      this._refreshTimer = setTimeout(this.doTokenRefresh, expiry - 60000)
    } else {
      // Expired or about to (e.g. lapsed while the machine slept) — try to refresh
      // now rather than immediately logging the user out.
      this.doTokenRefresh()
    }
  }

  // Perform the refresh, rescheduling on success and recovering on failure:
  // transient errors (network / 5xx — e.g. Wi-Fi still reconnecting right after
  // wake) retry shortly instead of killing the loop; only a definitive 401/400
  // (refresh token no longer valid) logs the user out.
  doTokenRefresh = () => {
    if (!this.props.auth || !this.props.auth.refreshToken) return
    this.props.authRefreshRequest(this.clearAuthTimeout, this.userLoggedOut)
      .then(() => { if (this._mounted) this.refreshToken() })
      .catch(err => {
        if (!this._mounted) return
        const status = err && err.status
        if (status === 401 || status === 400) {
          this.props.logout()
          this.props.expiredTokenLogout()
        } else {
          if (this._refreshTimer) clearTimeout(this._refreshTimer)
          this._refreshTimer = setTimeout(this.doTokenRefresh, 30000)
        }
      })
  }

  // When the tab becomes visible again (e.g. the machine woke from sleep), the
  // scheduled setTimeout may have been suspended and missed its window. Re-validate
  // immediately: refresh now if the token is expired or nearly so, otherwise
  // reschedule from the real remaining lifetime.
  handleVisibility = () => {
    if (document.visibilityState !== 'visible') return
    if (!this.props.auth || !this.props.auth.token || !this.props.auth.expires) return
    const expiry = moment.utc(this.props.auth.expires).diff(moment())
    if (!expiry || expiry < 120000) {
      this.doTokenRefresh()
    } else {
      this.refreshToken()
    }
  }

  fetchSubscription() {
    const { user, userActivePlan } = this.props
    let planId = PLAN_IDS.FREE
    if (userHasProUltimate(user)) planId = PLAN_IDS.PRO_ULTIMATE
    else if (userHasProAdvantage(user)) planId = PLAN_IDS.PRO_ADVANTAGE
    else if (userHasPro(user)) planId = PLAN_IDS.PRO
    userActivePlan({ id: planId })
  }

  render() {
    const { loading, selectedTabvalue } = this.props
    const { loaderSpinner } = this.state

    if (loading !== false) return null
    if (loaderSpinner) return SPLASH_SCREEN

    const isMapTab = selectedTabvalue === 'map'
    const tabStyle = isMapTab ? STYLES.tabHidden : STYLES.tabVisible
    const mapStyle = isMapTab ? STYLES.mapVisible : STYLES.mapHidden

    return (
      <div>
        <TopMenu />

        <React.Suspense fallback={null}>
          <div style={tabStyle}>
            {selectedTabvalue === 'huntplanner' && <HuntPlannerLandingPage />}
          </div>
          <div style={tabStyle}>
            {selectedTabvalue === 'recreationAccess' && <RecreationalAccess />}
          </div>
          <div style={tabStyle}>
            {selectedTabvalue === 'gearStore' && <GearStore />}
          </div>
          <div style={tabStyle}>
            {selectedTabvalue === 'artemis' && <ArtemisFullView />}
          </div>
        </React.Suspense>

        <div style={mapStyle}>
          <Map />
        </div>

        <MainUI />
        <MapMarkersUpdater fetchChanges={this.handleFetchChanges} />
        <AppMessage />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
  user: state.user,
  userSettings: state.userSettings,
  loading: state.loading,
  layersDictionary: state.layersDictionary,
  selectedTabvalue: state.selectedTab,
})

const mapDispatchToProps = dispatch => ({
  fetchChanges: () => dispatch(fetchChanges()),
  openMapDeepLink: link => dispatch(openMapDeepLink(link)),
  collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
  collapseRightToolbar: () => dispatch(collapseRightToolbar()),
  followingFetchRequest: () => dispatch(followingFetchRequest()),
  layersFetchRequest: () => dispatch(layersFetchRequest()),
  // Aliased import: a same-named property key makes Babel emit `function setLoading()`,
  // whose own name shadows the import and recurses forever (WEB-7031).
  setLoading: bool => dispatch(setLoadingAction(bool)),
  markerGroupsFetchRequest: () => dispatch(markerGroupsFetchRequest()),
  markerTypes105FetchRequest: () => dispatch(markerTypes105FetchRequest()),
  markerTypes35FetchRequest: () => dispatch(markerTypes35FetchRequest()),
  markerTypes25FetchRequest: () => dispatch(markerTypes25FetchRequest()),
  residenceGetRequest: () => dispatch(residenceGetRequest()),
  userFetchRequest: () => dispatch(userFetchRequest()),
  userSettingsGetRequest: () => dispatch(userSettingsGetRequest()),
  userSettingsSet: arr => dispatch(userSettingsSet(arr)),
  userSettingsUpdateRequest: (id, arr) => dispatch(userSettingsUpdateRequest(id, arr)),
  termsFetchRequest: () => dispatch(termsFetchRequest()),
  authRefreshRequest: () => dispatch(authRefreshRequest()),
  logout: () => dispatch(logout()),
  getStates: () => dispatch(getStates()),
  getNotifications: () => dispatch(getNotification()),
  // Aliased import, as with setLoading above (WEB-7031).
  fetchAppMessage: () => dispatch(fetchAppMessageAction()),
  expiredTokenLogout: () => dispatch(expiredTokenLogout()),
  removeCenterMarker: () => dispatch(removeCenterMarker()),
  weatherActionRemove: () => dispatch(weatherActionRemove()),
  selectedObjectIdNull: () => dispatch(selectedObjectIdNull()),
  selectedTabAction: payload => dispatch(selectedTabAction(payload)),
  userActivePlan: payload => dispatch(userActivePlan(payload)),
  expandMorefilterMenu: payload => dispatch(expandMorefilterMenu(payload)),
  huntPlannerRightMenuBarHide: () => dispatch(huntPlannerRightMenuBarHide()),
  isHuntPlannerEnable: () => dispatch(isHuntPlannerEnable()),
  isHuntPlannerDisable: () => dispatch(isHuntPlannerDisable()),
  setHuntPlannerMapReferenceNull: () => dispatch(setHuntPlannerMapReferenceNull()),
  setLandTrustMapReferenceNull: () => dispatch(setLandTrustMapReferenceNull()),
  userSubscriptionFetchRequest: () => dispatch(userSubscriptionFetchRequest()),
  unselected3dMapAction: () => dispatch(unselected3dMapAction()),
  openThreeDWelcomePopup: () => dispatch(openThreeDWelcomePopup()),
  huntPlannerActiveOptions: payload => dispatch(huntPlannerActiveOptions(payload)),
  fetchGetUnitDetails: payload => dispatch(fetchGetUnitDetails(payload)),
  huntPlannerRightMenuBarOpen: payload => dispatch(huntPlannerRightMenuBarOpen(payload)),
  startMapLoading: () => dispatch(startMapLoading()),
  stopMapLoading: () => dispatch(stopMapLoading()),
})

export default connect(mapStateToProps, mapDispatchToProps)(Landing)











// import React from 'react'
// import { connect } from 'react-redux'
// import { fetchChanges } from '../../../action/changes-last-called-actions'
// import { followingFetchRequest } from '../../../action/follow-list-actions'
// import { huntTypesFetchRequest } from '../../../action/hunt-types-actions'
// import { layersFetchRequest } from '../../../action/layer-actions'
// import { markerGroupsFetchRequest } from '../../../action/marker-group-actions'
// import { markerTypes105FetchRequest } from '../../../action/marker-types-105-actions'
// import { markerTypes35FetchRequest } from '../../../action/marker-types-35-actions'
// import { markerTypes25FetchRequest } from '../../../action/marker-types-25-actions'
// import { residenceGetRequest } from '../../../action/residence-actions'
// import { speciesFetchRequest } from '../../../action/species-actions'
// import { userFetchRequest } from '../../../action/user-actions'
// import { userSettingsGetRequest, userSettingsSet, userSettingsUpdateRequest } from '../../../action/user-settings-actions'
// import { weaponTypesFetchRequest } from '../../../action/weapon-types-actions'
// import { setLoading } from '../../../action/loading-actions'
// import Map from '../../map'
// import MainUI from '../../main-ui/'
// import { collapseLeftToolbar } from '../../../action/left-toolbar-state-actions'
// import { collapseRightToolbar } from '../../../action/right-toolbar-state-actions'
// import { isNil } from 'lodash'
// import { termsFetchRequest } from '../../../action/terms-actions'
// import moment from 'moment'
// import { authRefreshRequest, logout, expiredTokenLogout } from '../../../action/auth-actions'
// import { getNotification } from '../../../action/notification-actions'
// import { getStates } from '../../../action/state-actions'
// import { markerFetchRequest } from '../../../action/marker-actions'
// import { weatherActionRemove } from '../../../action/weather-action'
// import { removeCenterMarker } from '../../../action/center-marker-action'
// import { selectedObjectIdNull } from '../../../action/map-object-id'
// import MapMarkersUpdater from './realtime-update';
// import TopMenu from '../../top-menu'
// import { selectedTabAction } from '../../../action/selected-tab-action';
// import HuntPlannerLandingPage from '../../hunt-planner/landingpage/huntPlannerLandingPage';
// import { userActivePlan } from '../../../action/huntplanner-subscription-actions';
// import { userHasProUltimate, userHasPro, userHasProAdvantage } from '../../../lib/util'
// import { expandMorefilterMenu } from '../../../action/huntplanner-morefilters-actions'
// import { huntPlannerRightMenuBarHide } from '../../../action/huntplanner-menu-bar'
// import { fetchHuntPlannerLayersInitial } from '../../../action/huntplanner-state-Filter-actions'
// import { isHuntPlannerEnable, isHuntPlannerDisable } from '../../../action/isHuntPlannerDisable'
// import RecreationalAccess from '../../recreationAccess/landingpage/index'
// import { setHuntPlannerMapReferenceNull } from '../../../action/map-reference-huntplanner-action'
// import { setLandTrustMapReferenceNull } from '../../../action/map-reference-landtrust'
// import { userSubscriptionFetchRequest } from '../../../action/user-subscription-actions'
// import { unselected3dMapAction } from '../../../action/selected-3dmap'
// import GearStore from '../../gearStore/landingpage/index'
// import { openThreeDWelcomePopup } from '../../../action/threed-maps-actions'
// import { fetchUserDataSource } from '../../../action/artemis-actions/query-action'
// import { huntPlannerActiveOtions } from '../../../action/huntplanner-selected-options'
// import { fetchGetUnitDetails } from '../../../action/huntPlanner-unitdetails-actions'
// import { huntPlannerRightMenuBarOpen } from '../../../action/huntplanner-menu-bar'
// import { stopMapLoading, startMapLoading } from '../../../action/huntplanner-map-loader'

// const TRANSITION_STYLE = 'opacity 1000ms ease-in-out, visibility 0ms 1000ms'

// class Landing extends React.Component {
//   constructor(props) {
//     super(props)
//     this.refreshToken = this.refreshToken.bind(this)
//     this.handleFetchChanges = () => this.props.fetchChanges()
//     this.state = {
//       loaderSpinner: true,
//       timeCount: 0,
//       dontCall: false
//     }
//   }

//   scheduleAppDataLoad = () => {
//     this.props.collapseLeftToolbar()
//     this.props.collapseRightToolbar()
//     this.updateAuthToken()
//     this.props.userFetchRequest()
//       .then(() => this.props.followingFetchRequest())
//       .then(() => this.props.fetchHuntPlannerLayersInitial())
//       .then(() => this.props.userSettingsGetRequest())
//       .then(() => this.props.markerGroupsFetchRequest())
//       .then(() => this.props.weatherActionRemove())
//       .then(() => this.props.selectedTabAction('map'))
//       .then(() => this.props.isHuntPlannerDisable())
//       .then(() => this.props.unselected3dMapAction())
//       .then(() => this.props.openThreeDWelcomePopup())

//       .then(() => {
//         this.props.setLoading(false)
//         this.fetchSubscription()
//         this.setState({ loaderSpinner: false })
//       })
//     this.loadAppData()
//       .then(res => {
//         this.validateLayers()
//         this.props.getNotifications()
//         this.refreshToken()
//       })
//       .catch(err => {
//         if (err === 401) {
//           logout()
//           expiredTokenLogout()
//         } else {
//           this.fetchSubscription()
//           this.loadAppData()
//         }
//         console.log('Initialization error: ', err)
//       })

//   };

//   componentDidMount() {
//     const { setLoading } = this.props
//     setLoading(true)
//     // http://127.0.0.1:8080/?StateAbbreviation=CO&Species=Deer (Whitetail)&HuntTypeKey=over_the_counter~resident&unitName=49
//     // http://127.0.0.1:8080/?StateAbbreviation=CO&Species=Elk%20(Rocky%20Mtn)&HuntTypeKey=limited_drawing~resident&unitName=201

//     // 🔍 Check if page opened with deeplink query params
//     const urlParams = new URLSearchParams(window.location.search);
//     const hasDeeplinkParams =
//       urlParams.has('StateAbbreviation') &&
//       urlParams.has('Species') &&
//       urlParams.has('HuntTypeKey') &&
//       urlParams.has('unitName');

//     if (hasDeeplinkParams) {
//       const payload = {
//         StateAbbreviation: urlParams.get('StateAbbreviation') || '',
//         Species: urlParams.get('Species') || '',
//         HuntTypeKey: urlParams.get('HuntTypeKey') || '',
//         unitName: urlParams.get('unitName') || '',
//       };

//       this.setState({ loaderSpinner: false });
//       this.props.selectedTabAction('huntplanner')
//       this.props.huntPlannerActiveOtions(payload)
//       setLoading(false);

//       // Remove query parameters from URL without reloading the page
//       window.history.replaceState({}, document.title, window.location.pathname);

//       return;
//     }

//     setTimeout(() => {
//       this.scheduleAppDataLoad();
//     }, 3 * 1000);
//   }

//   componentDidUpdate(preProps) {
//     if (preProps.selectedTabvalue != this.props.selectedTabvalue) {
//       if (this.props.selectedTabvalue === 'map') {
//         this.props.setLandTrustMapReferenceNull();
//         this.props.setHuntPlannerMapReferenceNull()
//       }
//     }
//   }

//   updateAuthToken = () => {
//     const { expires } = this.props.auth;
//     const expirationTime = new Date(expires).getTime();
//     const currentTime = Date.now();
//     const timeDifference = expirationTime - currentTime;

//     if (timeDifference <= 0) {
//       this.props.logout()
//     }
//   }

//   validateLayers() {
//     const { userSettings, layersDictionary } = this.props
//     const oldActive = userSettings.find(setting => setting.settingId == 1),
//       oldFavorite = userSettings.find(setting => setting.settingId == 2),
//       validActive = oldActive.valueArray.filter(id => !isNil(layersDictionary[id])),
//       validFavorite = oldFavorite ? oldFavorite.valueArray.filter(id => !isNil(layersDictionary[id])) : []
//     if (oldActive.valueArray.length !== validActive.length || (oldFavorite && oldFavorite.valueArray.length !== validFavorite.length)) {
//       const copy = userSettings.map(setting => {
//         if (setting.settingId == 1) {
//           return { ...setting, value: JSON.stringify(validActive), valueArray: validActive }
//         } else if (setting.settingId == 2) {
//           return { ...setting, value: JSON.stringify(validFavorite), valueArray: validFavorite }
//         }
//         return setting
//       })
//       if (oldActive.valueArray.length !== validActive.length) {
//         this.props.userSettingsUpdateRequest(1, JSON.stringify(validActive))
//       }
//       if (oldFavorite && oldFavorite.valueArray.length !== validFavorite.length) {
//         this.props.userSettingsUpdateRequest(2, JSON.stringify(validFavorite))
//       }
//       this.props.userSettingsSet(copy)
//     }
//   }

//   refreshToken() {
//     const expiry = moment.utc(this.props.auth.expires).diff(moment())
//     if (expiry && expiry > 2000000000) {
//       setTimeout(() => {
//         this.refreshToken()
//       }, 1000000)
//     }
//     else {
//       if (expiry && expiry > 60000) {
//         setTimeout(() => {
//           this.props.authRefreshRequest(this.clearAuthTimeout, this.userLoggedOut)
//             .then(this.refreshToken)
//         }, expiry - 60000)
//       } else {
//         this.props.logout()
//         this.props.expiredTokenLogout()
//       }
//     }
//   }

//   loadAppData() {
//     const withTimeout = (fn, label) => () =>
//       Promise.race([
//         Promise.resolve().then(fn),
//         new Promise(res => setTimeout(res, 3000)),
//       ]).catch(err => {
//         console.warn(`loadAppData: "${label}" failed, skipping.`, err);
//       });

//     return new Promise((resolve, reject) => {
//       Promise.resolve()
//         .then(withTimeout(() => this.props.userFetchRequest(), 'userFetchRequest'))
//         .then(withTimeout(() => this.props.fetchUserDataSource(), 'fetchUserDataSource'))
//         .then(withTimeout(() => this.props.layersFetchRequest(), 'layersFetchRequest'))
//         .then(withTimeout(() => this.props.markerGroupsFetchRequest(), 'markerGroupsFetchRequest'))
//         .then(withTimeout(() => this.props.markerTypes25FetchRequest(), 'markerTypes25FetchRequest'))
//         .then(withTimeout(() => this.props.markerTypes35FetchRequest(), 'markerTypes35FetchRequest'))
//         .then(withTimeout(() => this.props.markerTypes105FetchRequest(), 'markerTypes105FetchRequest'))
//         .then(withTimeout(() => this.props.huntTypesFetchRequest(), 'huntTypesFetchRequest'))
//         .then(withTimeout(() => this.props.weaponTypesFetchRequest(), 'weaponTypesFetchRequest'))
//         .then(withTimeout(() => this.props.speciesFetchRequest(), 'speciesFetchRequest'))
//         .then(withTimeout(() => this.props.residenceGetRequest(), 'residenceGetRequest'))
//         .then(withTimeout(() => this.props.termsFetchRequest(), 'termsFetchRequest'))
//         .then(withTimeout(() => this.props.getStates(), 'getStates'))
//         .then(withTimeout(() => this.props.fetchChanges(), 'fetchChanges'))
//         .then(withTimeout(() => this.props.markerFetchRequest(), 'markerFetchRequest'))
//         .then(withTimeout(() => this.props.removeCenterMarker(), 'removeCenterMarker'))
//         .then(withTimeout(() => this.props.selectedObjectIdNull(), 'selectedObjectIdNull'))
//         .then(withTimeout(() => this.props.expandMorefilterMenu(false), 'expandMorefilterMenu'))
//         .then(withTimeout(() => this.props.huntPlannerRightMenuBarHide(), 'huntPlannerRightMenuBarHide'))
//         .then(withTimeout(() => this.props.isHuntPlannerEnable(), 'isHuntPlannerEnable'))
//         .then(withTimeout(() => this.props.userSubscriptionFetchRequest(), 'userSubscriptionFetchRequest'))
//         .then(() => {
//           resolve();
//           this.fetchSubscription();
//           this.props.setLoading(false);
//         })
//         .catch(error => {
//           console.error('Error in loadAppData:', error);
//           reject(error);
//         });
//     });
//   }

//   fetchSubscription() {
//     const { user, userActivePlan } = this.props;
//     let planId = null;
//     switch (true) {
//       case userHasProUltimate(user):
//         planId = 44;
//         break;
//       case userHasProAdvantage(user):
//         planId = 36;
//         break;
//       case userHasPro(user):
//         planId = 4;
//         break;
//       default:
//         planId = 0;
//     }
//     if (planId !== null) {
//       userActivePlan({ id: planId });
//     }
//   }

//   render() {
//     const { loading, selectedTabvalue } = this.props;
//     const { loaderSpinner } = this.state
//     const isMapTab = selectedTabvalue === 'map'
//     const tabStyle = {
//       display: isMapTab ? 'none' : 'flex',
//       opacity: isMapTab ? 0 : 1,
//       visibility: isMapTab ? 'hidden' : 'visible',
//       transition: TRANSITION_STYLE,
//     }
//     const mapStyle = {
//       display: isMapTab ? 'flex' : 'none',
//       opacity: isMapTab ? 1 : 0,
//       visibility: isMapTab ? 'visible' : 'hidden',
//       transition: TRANSITION_STYLE,
//     }

//     return loading === false ?
//       !loaderSpinner ? <div >
//         {/* Root file for huntplanner / top menu */}
//         <TopMenu />

//         {/* Root file for HuntPlannerLandingPage with smooth transition */}
//         <div style={tabStyle}>
//           {selectedTabvalue == 'huntplanner' && <HuntPlannerLandingPage />}
//         </div>

//         <div style={tabStyle}>
//           {selectedTabvalue == 'recreationAccess' && <RecreationalAccess />}
//         </div>

//         <div style={tabStyle}>
//           {selectedTabvalue == 'gearStore' && <GearStore />}
//         </div>

//         {/* Root file for Map with smooth transition */}
//         <div style={mapStyle}>
//           <Map />
//         </div>

//         {/* Root file for left toolbar */}
//         <MainUI />
//         {/* Auto api calls for objects for every 2 minutes */}
//         <MapMarkersUpdater fetchChanges={this.handleFetchChanges}></MapMarkersUpdater>
//       </div>
//         :
//         <div className="splash-screen">
//           <div className="splash-screen-spinner-container">
//             <div className="loader-spinner"></div>
//           </div>
//         </div>
//       : null
//   }
// }

// const mapStateToProps = state => ({
//   auth: state.auth,
//   user: state.user,
//   userSettings: state.userSettings,
//   loading: state.loading,
//   layersDictionary: state.layersDictionary,
//   selectedTabvalue: state.selectedTab,
// })

// const mapDispatchToProps = dispatch => ({
//   fetchChanges: () => dispatch(fetchChanges()),
//   collapseLeftToolbar: () => dispatch(collapseLeftToolbar()),
//   collapseRightToolbar: () => dispatch(collapseRightToolbar()),
//   followingFetchRequest: () => dispatch(followingFetchRequest()),
//   huntTypesFetchRequest: () => dispatch(huntTypesFetchRequest()),
//   layersFetchRequest: () => dispatch(layersFetchRequest()),
//   setLoading: bool => dispatch(setLoading(bool)),
//   markerGroupsFetchRequest: () => dispatch(markerGroupsFetchRequest()),
//   markerTypes105FetchRequest: () => dispatch(markerTypes105FetchRequest()),
//   markerTypes35FetchRequest: () => dispatch(markerTypes35FetchRequest()),
//   markerTypes25FetchRequest: () => dispatch(markerTypes25FetchRequest()),
//   speciesFetchRequest: () => dispatch(speciesFetchRequest()),
//   residenceGetRequest: () => dispatch(residenceGetRequest()),
//   userFetchRequest: () => dispatch(userFetchRequest()),
//   userSettingsGetRequest: () => dispatch(userSettingsGetRequest()),
//   weaponTypesFetchRequest: () => dispatch(weaponTypesFetchRequest()),
//   userSettingsSet: (arr) => dispatch(userSettingsSet(arr)),
//   userSettingsUpdateRequest: (id, arr) => dispatch(userSettingsUpdateRequest(id, arr)),
//   termsFetchRequest: () => dispatch(termsFetchRequest()),
//   authRefreshRequest: () => dispatch(authRefreshRequest()),
//   logout: () => dispatch(logout()),
//   getStates: () => dispatch(getStates()),
//   getNotifications: () => dispatch(getNotification()),
//   expiredTokenLogout: () => dispatch(expiredTokenLogout()),
//   markerFetchRequest: () => dispatch(markerFetchRequest()),
//   removeCenterMarker: () => dispatch(removeCenterMarker()),
//   weatherActionRemove: () => dispatch(weatherActionRemove()),
//   selectedObjectIdNull: () => dispatch(selectedObjectIdNull()),
//   selectedTabAction: (payload) => dispatch(selectedTabAction(payload)),
//   userActivePlan: (payload) => dispatch(userActivePlan(payload)),
//   expandMorefilterMenu: (payload) => dispatch(expandMorefilterMenu(payload)),
//   fetchHuntPlannerLayersInitial: () => dispatch(fetchHuntPlannerLayersInitial()),
//   huntPlannerRightMenuBarHide: () => dispatch(huntPlannerRightMenuBarHide()),
//   isHuntPlannerEnable: () => dispatch(isHuntPlannerEnable()),
//   isHuntPlannerDisable: () => dispatch(isHuntPlannerDisable()),
//   setHuntPlannerMapReferenceNull: () => dispatch(setHuntPlannerMapReferenceNull()),
//   setLandTrustMapReferenceNull: () => dispatch(setLandTrustMapReferenceNull()),
//   userSubscriptionFetchRequest: () => dispatch(userSubscriptionFetchRequest()),
//   unselected3dMapAction: () => dispatch(unselected3dMapAction()),
//   openThreeDWelcomePopup: () => dispatch(openThreeDWelcomePopup()),
//   fetchUserDataSource: () => dispatch(fetchUserDataSource()),
//   huntPlannerActiveOtions: (payload) => dispatch(huntPlannerActiveOtions(payload)),
//   fetchGetUnitDetails: (payload) => dispatch(fetchGetUnitDetails(payload)),
//   huntPlannerRightMenuBarOpen: (payload) => dispatch(huntPlannerRightMenuBarOpen(payload)),
//   startMapLoading: () => dispatch(startMapLoading()),
//   stopMapLoading: () => dispatch(stopMapLoading()),


// })

// export default connect(mapStateToProps, mapDispatchToProps)(Landing)
